import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Thermometer } from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { formatDateDMY } from '../../lib/dates';
import { useAuth } from '../../context/AuthContext';
import IceboxMap from '../../components/IceboxMap';
import IceboxTempBar from '../../components/IceboxTempBar';

export default function LiveAppOps() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [bind, setBind] = useState({ device_id: '', nurse_id: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: d } = await axios.get(`${config.API}/admin/live-ops`, { headers: getAuthHeaders() });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load live ops');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const sync = async () => {
    setSaving(true);
    try {
      await axios.post(`${config.API}/admin/mindlabs/sync`, {}, { headers: getAuthHeaders() });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Mindlabs sync failed — check API key');
    } finally {
      setSaving(false);
    }
  };

  const assign = async (e) => {
    e.preventDefault();
    if (!bind.device_id || !bind.nurse_id) return;
    setSaving(true);
    try {
      const nurse = (data?.nurses || []).find((n) => n.id === bind.nurse_id || n.user_id === bind.nurse_id);
      await axios.post(`${config.API}/admin/mindlabs/assign`, {
        device_id: bind.device_id.trim(),
        nurse_id: bind.nurse_id,
        nurse_name: nurse?.full_name || '',
      }, { headers: getAuthHeaders() });
      setBind({ device_id: '', nurse_id: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not bind icebox');
    } finally {
      setSaving(false);
    }
  };

  const kpis = data?.kpis || {};
  const iceboxes = data?.iceboxes || [];
  const selected = iceboxes.find((b) => b.device_id === selectedId) || iceboxes.find((b) => b.lat != null) || iceboxes[0];

  return (
    <div data-testid="live-app-ops">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>App Operations</h1>
          <p className="text-sm text-[#673c96] mt-1">Live bookings and icebox cold-chain (refreshes every 15s)</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={sync} disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#673c96]/20 text-[#673c96] text-sm">
            Sync Mindlabs
          </button>
          <button type="button" onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#673c96] text-white text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      {error && <p className="text-[#ee4036] text-sm mb-4">{error}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          ['Pending', kpis.pending],
          ['Active visits', kpis.active],
          ['Nurses available', kpis.nurses_available],
          ['Iceboxes today', iceboxes.filter((b) => b.status !== 'idle').length],
        ].map(([label, value]) => (
          <div key={label} className="bg-white rounded-xl border border-[#673c96]/10 p-4">
            <div className="text-3xl font-bold text-[#f37721]">{value ?? '—'}</div>
            <div className="text-xs text-[#673c96] mt-1">{label}</div>
          </div>
        ))}
      </div>

      {iceboxes.some((b) => b.shock) && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="jerk-alert-banner">
          Icebox jerk / drop detected. Super admin: check whether a nurse dropped the icebox.
        </div>
      )}
      <h2 className="font-semibold text-[#311b52] mb-3 flex items-center gap-2">
        <Thermometer className="w-4 h-4" /> Iceboxes
      </h2>
      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-[#673c96]/10 overflow-hidden">
          <IceboxMap lat={selected?.lat} lng={selected?.lng} label={selected?.label || 'Icebox'} height={280} />
          {selected && (
            <div className="p-4 text-sm space-y-2">
              <p className="font-semibold text-[#311b52]">{selected.nurse_name || 'Unassigned'} · {selected.label}</p>
              <IceboxTempBar temp={selected.temp} lastUpdated={selected.last_updated} />
              {selected.shock && (
                <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="icebox-shock">
                  Jerk / drop detected on this icebox. Super admin only — check if the nurse dropped the box.
                </p>
              )}
              {selected.reconnecting && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Reconnecting to the icebox. Last temperature is still shown. A short signal pause is not a cold-chain failure by itself.
                </p>
              )}
              {(selected.lid_open || selected.battery != null) && (
                <p className="text-xs text-[#6b7280]">
                  {selected.lid_open ? 'Lid open' : ''}
                  {selected.lid_open && selected.battery != null ? ' · ' : ''}
                  {selected.battery != null ? `Battery ${selected.battery}%` : ''}
                </p>
              )}
              <p className="text-xs text-[#6b7280]">{selected.address || 'Location pending'}</p>
              {selected.next_booking && (
                <p className="text-xs text-[#673c96]">
                  Next: {selected.next_booking.beneficiary_name || selected.next_booking.customer_name} · {selected.next_booking.status}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <form onSubmit={assign} className="bg-white rounded-xl border border-[#673c96]/10 p-4 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[120px]">
              <label className="text-[10px] uppercase font-semibold text-[#673c96]/70">Device ID</label>
              <input value={bind.device_id} onChange={(e) => setBind({ ...bind, device_id: e.target.value })}
                placeholder="AA3630" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-[10px] uppercase font-semibold text-[#673c96]/70">Nurse</label>
              <select value={bind.nurse_id} onChange={(e) => setBind({ ...bind, nurse_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm bg-white">
                <option value="">Select</option>
                {(data?.nurses || []).map((n) => (
                  <option key={n.id || n.user_id} value={n.user_id || n.id}>{n.full_name}</option>
                ))}
              </select>
            </div>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#673c96] text-white text-sm font-semibold">
              Bind for today
            </button>
          </form>
          <div className="bg-white rounded-xl border overflow-auto max-h-80">
            <table className="w-full text-sm" data-testid="icebox-table">
              <thead>
                <tr className="text-left text-[#673c96] border-b">
                  <th className="p-3">Device</th><th>Nurse</th><th>Temp</th><th>Alerts</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {iceboxes.map((box) => (
                  <tr key={box.device_id} className={`border-t cursor-pointer ${selected?.device_id === box.device_id ? 'bg-[#fdfcff]' : ''}`}
                    onClick={() => setSelectedId(box.device_id)}>
                    <td className="p-3 font-mono text-xs">{box.label}</td>
                    <td>{box.nurse_name || '—'}</td>
                    <td className="p-3 min-w-[140px]"><IceboxTempBar temp={box.temp} lastUpdated={box.last_updated} compact /></td>
                    <td className="text-xs">
                      {box.shock ? <span className="text-red-700 font-semibold">Jerk</span> : null}
                      {box.reconnecting ? <span className="text-amber-700 font-semibold block">Reconnecting</span> : null}
                      {!box.shock && !box.reconnecting ? '—' : null}
                    </td>
                    <td className="capitalize">{(box.status || '').replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {iceboxes.length === 0 && (
              <p className="p-4 text-[#673c96] text-sm">No icebox telemetry yet. Bind a Go device or wait for the Mindlabs webhook.</p>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-semibold text-[#311b52] mb-3">Pending confirmation</h2>
      <div className="bg-white rounded-xl border overflow-auto mb-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#673c96] border-b">
              <th className="p-3">ID</th><th>Customer</th><th>Service</th><th>Date</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.pending_bookings || []).map((b) => (
              <tr key={b.id} className="border-t">
                <td className="p-3 font-mono text-xs">{b.id}</td>
                <td>{b.customer_name}</td>
                <td>{b.service_name || b.vaccine_name}</td>
                <td>{formatDateDMY(b.preferred_date || b.booking_date)}</td>
                <td>{b.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data?.pending_bookings || []).length === 0 && <p className="p-4 text-[#673c96] text-sm">No pending bookings.</p>}
      </div>

      <h2 className="font-semibold text-[#311b52] mb-3">Active visits</h2>
      <div className="bg-white rounded-xl border overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#673c96] border-b">
              <th className="p-3">ID</th><th>Customer</th><th>Nurse</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.active_visits || []).map((b) => (
              <tr key={b.id} className="border-t">
                <td className="p-3 font-mono text-xs">{b.id}</td>
                <td>{b.customer_name}</td>
                <td>{b.nurse_name || '—'}</td>
                <td>{b.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data?.active_visits || []).length === 0 && <p className="p-4 text-[#673c96] text-sm">No active visits.</p>}
      </div>
    </div>
  );
}
